import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft, CheckCircle2, ChevronRight, ChevronLeft,
  BookOpen, Award, Clock, Loader2, HelpCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CourseViewer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [track, setTrack] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [completing, setCompleting] = useState(false);

  const loadCourse = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/university/courses/${courseId}`, { headers: { Authorization: `Bearer ${token}` } });
      setCourse(res.data.course);
      setLessons(res.data.lessons);
      setTrack(res.data.track);
      const firstIncomplete = res.data.lessons.findIndex(l => !l.completed);
      if (firstIncomplete >= 0) setActiveIdx(firstIncomplete);
    } catch (err) { toast.error('Failed to load course'); }
    finally { setLoading(false); }
  }, [courseId, token]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  const currentLesson = lessons[activeIdx];
  const completedCount = lessons.filter(l => l.completed).length;
  const pct = lessons.length > 0 ? Math.round((completedCount / lessons.length) * 100) : 0;

  const completeLesson = async () => {
    if (!currentLesson) return;
    const hasQuiz = currentLesson.quiz_questions?.length > 0;

    if (hasQuiz && !quizSubmitted) {
      const total = currentLesson.quiz_questions.length;
      const answered = Object.keys(quizAnswers).length;
      if (answered < total) { toast.error('Please answer all questions'); return; }
      setQuizSubmitted(true);
      return;
    }

    let score = null;
    if (hasQuiz) {
      const total = currentLesson.quiz_questions.length;
      let correct = 0;
      currentLesson.quiz_questions.forEach((q, i) => {
        if (quizAnswers[i] === q.correct) correct++;
      });
      score = Math.round((correct / total) * 100);
      if (score < 70) {
        toast.error(`Score: ${score}%. You need 70% to pass. Try again!`);
        setQuizSubmitted(false);
        setQuizAnswers({});
        return;
      }
    }

    setCompleting(true);
    try {
      const res = await axios.post(
        `${API}/university/lessons/${currentLesson.lesson_id}/complete`,
        { score },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.certification_issued) {
        toast.success('Certification earned! Track completed!');
      } else if (res.data.course_complete) {
        toast.success('Course completed!');
      } else {
        toast.success('Lesson completed!');
      }
      setQuizAnswers({});
      setQuizSubmitted(false);
      await loadCourse();
      if (activeIdx < lessons.length - 1) setActiveIdx(activeIdx + 1);
    } catch (err) { toast.error('Failed to save progress'); }
    finally { setCompleting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse text-muted-foreground text-sm">Loading course...</div>
    </div>
  );

  if (!course) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <p className="text-muted-foreground">Course not found</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="course-viewer">
      {/* Top Bar */}
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/university')} className="text-muted-foreground shrink-0" data-testid="back-to-eu">
              <ArrowLeft className="h-4 w-4 mr-1" /> EU
            </Button>
            <div className="hidden sm:block h-5 w-px bg-muted" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-muted-foreground tracking-wide">{track?.name}</p>
              <p className="text-base font-bold text-foreground truncate">{course.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{completedCount}/{lessons.length} lessons</span>
            <Progress value={pct} className="w-24 h-1.5" />
            <span className="text-xs font-medium text-foreground">{pct}%</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 flex gap-6">
        {/* Sidebar - Lesson List */}
        <div className="hidden lg:block w-80 shrink-0">
          <div className="sticky top-20 space-y-1.5">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 px-2">Lessons</p>
            {lessons.map((lesson, idx) => (
              <button
                key={lesson.lesson_id}
                onClick={() => { setActiveIdx(idx); setQuizAnswers({}); setQuizSubmitted(false); }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                  idx === activeIdx
                    ? 'bg-indigo-500/10 border border-indigo-500/30'
                    : 'hover:bg-muted/50 border border-transparent'
                }`}
                data-testid={`lesson-nav-${idx}`}
              >
                {lesson.completed ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                ) : idx === activeIdx ? (
                  <BookOpen className="h-5 w-5 text-indigo-400 shrink-0" />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-border shrink-0" />
                )}
                <div className="min-w-0">
                  <p className={`text-sm leading-snug ${idx === activeIdx ? 'text-foreground font-bold' : 'text-muted-foreground font-medium'}`}>
                    {lesson.title}
                  </p>
                  {lesson.quiz_questions?.length > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">
                      {lesson.quiz_questions.length} questions
                      {lesson.score != null && <span className="text-emerald-400 ml-1">({lesson.score}%)</span>}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {currentLesson && (
            <>
              {/* Lesson Header */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-[10px] text-muted-foreground border-border">
                    Lesson {activeIdx + 1} of {lessons.length}
                  </Badge>
                  {currentLesson.quiz_questions?.length > 0 && (
                    <Badge className="bg-indigo-500/20 text-indigo-400 text-[10px]">
                      <HelpCircle className="h-2.5 w-2.5 mr-1" /> Quiz
                    </Badge>
                  )}
                  {currentLesson.completed && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">
                      <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Completed
                    </Badge>
                  )}
                </div>
                <h1 className="text-3xl font-extrabold text-foreground tracking-tight leading-tight">{currentLesson.title}</h1>
              </div>

              {/* Lesson Content */}
              <Card className="bg-muted/60 border-border mb-6">
                <CardContent className="p-6 sm:p-10">
                  <div
                    className="prose prose-invert prose-xl max-w-none
                      prose-headings:font-extrabold prose-headings:tracking-tight
                      prose-h1:text-4xl prose-h1:text-foreground prose-h1:mt-12 prose-h1:mb-6
                      prose-h2:text-3xl prose-h2:text-foreground prose-h2:mt-16 prose-h2:mb-6 prose-h2:pb-4
                      prose-h3:text-2xl prose-h3:text-foreground prose-h3:mt-12 prose-h3:mb-5
                      prose-h4:text-xl prose-h4:text-foreground prose-h4:mt-10 prose-h4:mb-4
                      prose-p:text-xl prose-p:text-foreground prose-p:leading-relaxed prose-p:mb-6
                      prose-li:text-xl prose-li:text-foreground prose-li:leading-relaxed prose-li:mb-3
                      prose-ul:my-6 prose-ul:space-y-1.5 prose-ol:my-6 prose-ol:space-y-1.5
                      prose-strong:text-foreground prose-strong:font-semibold
                      prose-em:text-indigo-300
                      prose-code:text-indigo-300 prose-code:bg-muted prose-code:px-2 prose-code:py-1 prose-code:rounded-md prose-code:text-base
                      prose-table:border-border prose-table:my-8
                      prose-th:text-foreground prose-th:font-bold prose-th:border-border prose-th:p-4 prose-th:text-base prose-th:bg-muted/50
                      prose-td:text-foreground prose-td:border-border prose-td:p-4 prose-td:text-base
                      prose-a:text-indigo-400 prose-a:underline
                      prose-blockquote:border-indigo-500/30 prose-blockquote:bg-indigo-500/5 prose-blockquote:py-2 prose-blockquote:px-5 prose-blockquote:rounded-r-lg prose-blockquote:text-lg"
                    style={{ fontSize: '20px', lineHeight: '1.85' }}
                    dangerouslySetInnerHTML={{ __html: currentLesson.content }}
                    data-testid="lesson-content"
                  />
                </CardContent>
              </Card>

              {/* Quiz Section */}
              {currentLesson.quiz_questions?.length > 0 && (
                <Card className="bg-muted/60 border-border mb-6" data-testid="quiz-section">
                  <CardContent className="p-6 sm:p-8">
                    <h3 className="text-xl font-extrabold text-foreground mb-6 flex items-center gap-2">
                      <HelpCircle className="h-5 w-5 text-indigo-400" />
                      Knowledge Check
                      {quizSubmitted && <span className="text-sm text-muted-foreground font-normal">(Review your answers)</span>}
                    </h3>
                    <div className="space-y-8">
                      {currentLesson.quiz_questions.map((q, qi) => (
                        <div key={qi} className="space-y-3">
                          <p className="text-lg font-bold text-foreground">{qi + 1}. {q.question}</p>
                          <div className="grid gap-2 pl-1">
                            {q.options.map((opt, oi) => {
                              const selected = quizAnswers[qi] === oi;
                              const isCorrect = q.correct === oi;
                              let cls = 'bg-muted/50 border-border text-muted-foreground hover:bg-muted hover:border-border';
                              if (selected && !quizSubmitted) cls = 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300';
                              if (quizSubmitted && isCorrect) cls = 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300';
                              if (quizSubmitted && selected && !isCorrect) cls = 'bg-red-500/20 border-red-500/40 text-red-300';

                              return (
                                <button
                                  key={oi}
                                  onClick={() => { if (!quizSubmitted) setQuizAnswers(p => ({ ...p, [qi]: oi })); }}
                                  className={`flex items-center gap-3 p-4 rounded-lg border text-left text-base transition-all ${cls}`}
                                  disabled={quizSubmitted}
                                  data-testid={`quiz-opt-${qi}-${oi}`}
                                >
                                  <span className="w-6 h-6 rounded-full border border-current flex items-center justify-center shrink-0 text-xs font-bold">
                                    {String.fromCharCode(65 + oi)}
                                  </span>
                                  <span className="font-medium">{opt}</span>
                                  {quizSubmitted && isCorrect && <CheckCircle2 className="h-4 w-4 ml-auto text-emerald-400" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action Bar */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setActiveIdx(Math.max(0, activeIdx - 1)); setQuizAnswers({}); setQuizSubmitted(false); }}
                  disabled={activeIdx === 0}
                  className="text-muted-foreground"
                  data-testid="prev-lesson-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>

                <div className="flex items-center gap-2">
                  {!currentLesson.completed && (
                    <Button
                      onClick={completeLesson}
                      disabled={completing}
                      className="bg-indigo-500 hover:bg-indigo-600"
                      data-testid="complete-lesson-btn"
                    >
                      {completing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> :
                        currentLesson.quiz_questions?.length > 0 && !quizSubmitted ? (
                          <><HelpCircle className="h-4 w-4 mr-1" /> Submit Quiz</>
                        ) : (
                          <><CheckCircle2 className="h-4 w-4 mr-1" /> Complete Lesson</>
                        )
                      }
                    </Button>
                  )}

                  {activeIdx < lessons.length - 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setActiveIdx(activeIdx + 1); setQuizAnswers({}); setQuizSubmitted(false); }}
                      className="text-muted-foreground border-border"
                      data-testid="next-lesson-btn"
                    >
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}

                  {activeIdx === lessons.length - 1 && pct === 100 && (
                    <Button
                      onClick={() => navigate('/university')}
                      className="bg-emerald-500 hover:bg-emerald-600"
                      data-testid="back-to-dashboard-btn"
                    >
                      <Award className="h-4 w-4 mr-1" /> Back to Dashboard
                    </Button>
                  )}
                </div>
              </div>

              {/* Mobile Lesson Nav */}
              <div className="lg:hidden mt-6">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">All Lessons</p>
                <div className="flex gap-1.5 overflow-x-auto pb-2">
                  {lessons.map((lesson, idx) => (
                    <button
                      key={lesson.lesson_id}
                      onClick={() => { setActiveIdx(idx); setQuizAnswers({}); setQuizSubmitted(false); }}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-xs transition-all ${
                        idx === activeIdx
                          ? 'bg-indigo-500 text-foreground'
                          : lesson.completed
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
